import { KitText } from '@omni/kit/components';
import KitCallout from '@omni/kit/components/KitCallout';
import KitCheckmark from '@omni/kit/components/KitCheckmark';
import { useShellContext } from '@omni/kit/contexts/ShellContext';
import { SizeClass, useSizeClass } from '@omni/kit/contexts/SizeClassContext';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import { colorForScheme } from '@omni/kit/theming/Theming';
import { useEmojiPickerContext } from '@omni/media/liveChat/shared/context/EmojiPickerContext';
import { GroupChannel } from '@sendbird/chat/groupChannel';
import autosize from 'autosize';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { Keyboard, Platform, StyleSheet, TextInput, View } from 'react-native';
import ParsedText from 'react-native-parsed-text';
import { useDispatch, useSelector } from 'react-redux';

import useGroupEventCallout from '../../../hooks/useGroupEventCallout';
import { getMemberList } from '../../../shared/redux/actions/ChatActions';
import {
  channelSelector,
  memberListSelector,
  sendToChannelSelector,
} from '../../../shared/redux/selectors';
import { MessageType } from '../../../shared/redux/types';
import WebActionBar from '../../../web/scenes/channel/components/WebActionBar';
import {
  ChannelMember,
  CustomMessageData,
  SendbirdFileMessage,
  SendbirdUserMessage,
} from '../../Types';
import ActionBar from './ActionBar';
import MentionList from './MentionList';
import { MessageInputIcon as Icon } from './MessageInputIcon';
import MessageTypeActionSheet from './MessageTypeActionSheet';

interface WebInputProps {
  actionShowNonText: boolean;
  hideActionBar?: boolean;
  onBackspace?: () => void;
  onChangeText?: (text: string) => void;
  onEmojiSelected?: (e: string) => void;
  onEnter?: () => void;
  placeholder: string;
  value: string;
  isManager?: boolean;
  onSelectionChange?: (pos: number) => void;
}

const WebInput = React.forwardRef<HTMLTextAreaElement, WebInputProps>(
  (
    {
      actionShowNonText,
      hideActionBar,
      onBackspace,
      onChangeText,
      onEmojiSelected,
      onEnter,
      placeholder,
      value,
      isManager,
      onSelectionChange,
    },
    ref
  ) => (
    <div style={webStyles.inputContainer}>
      <textarea
        onChange={({ target }) => {
          onChangeText?.(target.value);
        }}
        onClick={(e) => {
          onSelectionChange?.(e.currentTarget.selectionStart);
        }}
        onKeyUp={(e) => {
          onSelectionChange?.(e.currentTarget.selectionStart);
        }}
        onSelect={(e) => {
          onSelectionChange?.(e.currentTarget.selectionStart);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            onEnter?.();
          } else if (event.key === 'Backspace') {
            onBackspace?.();
          }
        }}
        placeholder={placeholder}
        ref={ref}
        rows={1}
        style={webStyles.input}
        value={value}
        data-testid='web-message-input'
      />
      {!hideActionBar ? (
        <WebActionBar
          showNonText={actionShowNonText}
          onEmojiSelected={onEmojiSelected}
          showGroupEvent={isManager}
        />
      ) : null}
    </div>
  )
);

export type { IFile } from './types';

export interface MessageInputProps {
  autoFocus?: boolean;
  channelName?: string;
  disabled?: boolean;
  hideDrawer?: boolean;
  isEditing: boolean;
  isThread?: boolean;
  onCancelEditingPress: () => void;
  onChangeText: (value: string) => void;
  onConfirmEditingPress: () => void;
  onMediaMessageSend?: () => void;
  onPrayerRequestPress?: () => void;
  onSendPress: (mentions: string[]) => void;
  onToggleSendToChannel?: () => void;
  selectedMessage?: SendbirdUserMessage | SendbirdFileMessage | null;
  textChanged: boolean;
  value: string;
}

export default function MessageInput({
  // Data
  autoFocus,
  channelName,
  disabled,
  hideDrawer,
  isEditing,
  isThread,
  selectedMessage,
  textChanged,
  value,

  // Methods
  onCancelEditingPress,
  onChangeText,
  onConfirmEditingPress,
  onMediaMessageSend,
  onSendPress,
  onToggleSendToChannel,
}: MessageInputProps): ReactElement {
  const { t } = useTranslation(['messaging']);

  /** Filtered list of members in the popup. */
  const [mentionList, setMentionList] = useState<ChannelMember[]>([]);
  /** User IDs that get sent to sendbird. */
  const [mentionedUsers, setMentionedUsers] = useState<string[]>([]);
  /** Mentioned users in this message, for rendering purposes. */
  const [mentionedUserObjects, setMentionedUserObjects] = useState<
    ChannelMember[]
  >([]);
  const [addonShowing, setAddonShowing] = useState(false);
  const menuButtonRef = React.createRef<View>();
  const [calloutDimensions, setCalloutDimensions] = useState({
    x: 0,
    y: 0,
    h: 0,
  });

  const { sizeClass } = useSizeClass();
  const members = useSelector(memberListSelector);
  const sendToChannel = useSelector(sendToChannelSelector);
  const channel = useSelector(channelSelector) as GroupChannel;
  const isManager =
    channel?.myRole === 'operator' && channel?.customType !== 'direct';

  const { app } = useShellContext();

  const inputRef = useRef<TextInput | null>(null);
  const webInput = useRef<HTMLTextAreaElement | null>(null);
  const [cursorPosition, setCursorPosition] = useState(value.length);
  const { selectedEmoji, setIsEmojiPickerOpen } = useEmojiPickerContext();
  const { showGroupEventCallout, setShowGroupEventCallout } =
    useGroupEventCallout(isManager);

  const dispatch = useDispatch();

  // Reset mention list when channel changes
  useEffect(() => {
    setMentionList([]);
    setMentionedUsers([]);
    setMentionedUserObjects([]);
    if (channel?.url) {
      dispatch(getMemberList(channel.url));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel?.url]);

  const handleMention = (member: ChannelMember) => {
    let newValue = value;
    const lastIndexOfAt = newValue.lastIndexOf('@');
    newValue = `${newValue.slice(0, lastIndexOfAt + 1)}${member.nickname} `;
    onChangeText(newValue);
    setMentionList([]);
    inputRef?.current?.focus();

    if (!mentionedUsers.includes(member.userId)) {
      const newMentionedUsers = [...mentionedUsers, member.userId];
      setMentionedUsers(newMentionedUsers);
      const newMentionedUserObjects = [...mentionedUserObjects, member];
      setMentionedUserObjects(newMentionedUserObjects);
    }
  };

  const handleSend = () => {
    setMentionList([]);
    setMentionedUsers([]);
    setMentionedUserObjects([]);
    onSendPress(mentionedUsers);
  };

  const onBackspace = () => {
    if (value.charAt(value.length - 1) === '@') {
      setMentionList([]);
    }

    mentionedUserObjects.forEach((user) => {
      if (value.includes(user.nickname)) return;

      const newMentionedUsers = mentionedUsers.filter((u) => u !== user.userId);
      const newMentionedUserObjects = mentionedUserObjects.filter(
        (userObject) => userObject.userId !== user.userId
      );
      setMentionedUsers(newMentionedUsers);
      setMentionedUserObjects(newMentionedUserObjects);
    });
  };

  const isWeb = Platform.OS === 'web';
  const isSmall = sizeClass === SizeClass.Small;

  useEffect(() => {
    if (isWeb && webInput.current) {
      autosize(webInput.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isWeb && webInput.current) {
      autosize.update(webInput.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (!isEditing) {
      inputRef?.current?.blur();

      return;
    }

    if (!selectedMessage) inputRef?.current?.focus();
  }, [isEditing, selectedMessage]);

  useEffect(() => {
    if (
      (value.slice(-2) === ' @' || value === '@') &&
      value.charAt(value.length - 1) === '@'
    ) {
      // Filter out users who are already mentioned
      const availableMembers = members.filter(
        (m) => !mentionedUsers.includes(m.userId)
      );
      setMentionList(availableMembers);
    }

    if (!value.includes('@') || !canSend) setMentionList([]);

    const lastIndexOfAt = value.lastIndexOf('@');

    if (
      lastIndexOfAt >= 0 &&
      (value.slice(lastIndexOfAt - 1, lastIndexOfAt + 1) === ' @' ||
        lastIndexOfAt === 0)
    ) {
      const query = value.slice(lastIndexOfAt + 1);
      // Filter out users who are already mentioned AND match the search query
      const filteredMembers = members.filter(
        (m) =>
          m.nickname.toLowerCase().includes(query.toLowerCase()) &&
          !mentionedUsers.includes(m.userId)
      );
      setMentionList(filteredMembers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members, value, mentionedUsers]);

  useEffect(() => {
    if (selectedEmoji) {
      const start = value.slice(0, cursorPosition);
      const end = value.slice(cursorPosition);
      const newText = `${start}${selectedEmoji.emoji}${end}`;
      onChangeText(newText);
      setCursorPosition(cursorPosition + selectedEmoji.emoji.length);

      setIsEmojiPickerOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmoji]);

  const inputProps = {
    autoFocus,
    multiline: true,
    onChangeText,
    placeholderTextColor: Colors.N400,
  };

  const placeholder = isThread
    ? 'Reply to thread...'
    : channelName
    ? `Message ${channelName}`
    : 'Message';

  const canSend = !disabled && value.length;
  const canUpdate = canSend && textChanged;

  const groupEventsEnabled = isManager;

  const onEmojiSelected = (emoji: string) => {
    const newText =
      value.slice(0, cursorPosition) + emoji + value.slice(cursorPosition);
    onChangeText(newText);
    setCursorPosition(cursorPosition + emoji.length);

    setTimeout(() => {
      if (webInput.current) {
        webInput.current.focus();
        webInput.current.setSelectionRange(
          cursorPosition + emoji.length,
          cursorPosition + emoji.length
        );
      }
    }, 0);

    setIsEmojiPickerOpen(false);
  };

  return (
    <>
      <View>
        <View style={{ position: 'relative' }}>
          <MentionList list={mentionList} mentionMember={handleMention} />
        </View>
        <View
          style={styles.inputContainer}
          ref={menuButtonRef}
          onLayout={(event) => {
            const { x, y, height } = event.nativeEvent.layout;

            if (Platform.OS === 'web') {
              setCalloutDimensions({
                x: x + 4,
                y: -(y + height),
                h: height,
              });
            }
          }}
        >
          {isWeb ? (
            <WebInput
              hideActionBar={hideDrawer || isSmall}
              onBackspace={onBackspace}
              onChangeText={onChangeText}
              onEmojiSelected={onEmojiSelected}
              onSelectionChange={setCursorPosition}
              actionShowNonText={!isEditing ? !canSend : false}
              onEnter={() => {
                if (isEditing) {
                  onConfirmEditingPress();
                } else {
                  handleSend();
                }
              }}
              placeholder={placeholder}
              ref={webInput}
              value={value}
              isManager={isManager}
            />
          ) : (
            // Mobile Input
            <TextInput
              {...inputProps}
              onKeyPress={({ nativeEvent }) => {
                if (nativeEvent.key === 'Backspace') onBackspace();
              }}
              onSelectionChange={({ nativeEvent }) => {
                setCursorPosition(nativeEvent.selection.start);
              }}
              placeholder={
                placeholder && placeholder.length > 25
                  ? placeholder.slice(0, 25) + '...'
                  : placeholder
              }
              ref={inputRef}
              style={styles.textInput}
            >
              <ParsedText
                parse={[
                  ...((mentionedUserObjects?.map((user) => ({
                    pattern: new RegExp(`@${user.nickname}`),
                    style: { color: Colors.brand, fontWeight: 'bold' },
                  })) || []) as Readonly<
                    Array<{ pattern: RegExp; style: Record<string, any> }>
                  >),
                  { pattern: /\*.*\*/, style: { fontWeight: 'bold' } },
                  { pattern: /_.*_/, style: { fontStyle: 'italic' } },
                ]}
              >
                {value}
              </ParsedText>
            </TextInput>
          )}
          {isSmall || !isWeb ? (
            <Icon
              icon='emoji'
              onPress={() => {
                setIsEmojiPickerOpen(true);
                Keyboard.dismiss();
              }}
            />
          ) : null}

          {isEditing ? (
            <Icon icon='remove' onPress={onCancelEditingPress} />
          ) : null}

          {isEditing ? (
            <Icon
              icon='check'
              color={canUpdate ? Colors.brand : undefined}
              onPress={canUpdate ? onConfirmEditingPress : undefined}
            />
          ) : canSend ? (
            <Icon icon='send' color={Colors.brand} onPress={handleSend} />
          ) : !(Platform.OS === 'web' && !isSmall) ? (
            <Icon
              border
              icon={addonShowing ? 'remove-s' : 'add-s'}
              onPress={() => setAddonShowing((prev) => !prev)}
              setCalloutDimensions={setCalloutDimensions}
            />
          ) : null}
        </View>
        {showGroupEventCallout ? (
          <KitCallout
            anchorX={calloutDimensions.x}
            anchorY={calloutDimensions.y}
            body={t('groups:keepYourGroupConnectedWithEvents')}
            dismiss={() => setShowGroupEventCallout(false)}
            animate
            delay={300}
            calloutWidth={320}
            calloutAbsolutePosition={{
              right:
                Platform.OS === 'web'
                  ? calloutDimensions.x
                  : calloutDimensions.x + 10,
              top:
                Platform.OS === 'web'
                  ? calloutDimensions.y + 100
                  : -(calloutDimensions.y + 44),
            }}
            anchorAbsolutePosition={{ bottom: -24, right: -2 }}
          />
        ) : null}
        {/* Custom message action bar, only applicable in small-screen web */}
        {addonShowing && !isEditing && isWeb && isSmall ? (
          <ActionBar
            onMessageSent={onMediaMessageSend}
            showGroupEvent={groupEventsEnabled}
          />
        ) : null}

        {isThread && onToggleSendToChannel && channelName ? (
          <View style={styles.checkmarkContainer}>
            <KitCheckmark
              checked={sendToChannel}
              onToggle={onToggleSendToChannel}
            />
            <KitText
              black
              fontSize={14}
              numberOfLines={1}
              semiBold
              style={styles.checkmarkLabel}
            >
              {t('messaging:alsoSendToChannelName', { channelName })}
            </KitText>
          </View>
        ) : null}

        {!isWeb ? (
          <MessageTypeActionSheet
            isVisible={addonShowing}
            onMessageSent={onMediaMessageSend}
            setVisible={setAddonShowing}
            showGroupEvent={groupEventsEnabled}
          />
        ) : null}
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  checkmarkContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    marginLeft: Spacing.xs,
    marginRight: Spacing.m,
    overflow: 'hidden',
  },
  checkmarkLabel: {
    flex: 1,
  },
  inputContainer: {
    alignItems: 'flex-end',
    backgroundColor: Colors.N100,
    borderRadius: 30,
    flexDirection: 'row',
    marginHorizontal: Spacing.m,
    marginVertical: Spacing.s,
    overflow: 'hidden',
    paddingLeft: Spacing.l,
    paddingRight: Spacing.m,
    paddingVertical: Spacing.m,
    position: 'relative',
  },
  textInput: {
    ...Platform.select({
      android: { marginVertical: -Spacing.xs },
      ios: { marginTop: -Spacing.xs },
      web: { outlineWidth: 0 },
    }),
    color: colorForScheme({ default: Colors.N900 }),
    flex: 1,
    fontSize: 16,
    maxHeight: 128,
    padding: 0,
  },
});

const webStyles = {
  input: {
    backgroundColor: 'transparent',
    borderWidth: 0,
    boxSizing: 'border-box',
    color: Colors.N900,
    flex: 1,
    fontFamily: 'inherit',
    fontSize: '15px',
    lineHeight: '1.2rem',
    maxHeight: 128,
    outlineWidth: 0,
    padding: 0,
    resize: 'none',
    width: '100%',
  },
  inputContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
  },
} as const;
